// src/auth/PrivateRoute.js

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Navigate,
  useLocation,
} from 'react-router-dom';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  getUserInfo,
} from '../auth/authService';

import {
  refreshAccessTokenRequest,
} from '../api/axios';

import {
  setUser,
  logout,
  setCompanyID,
  setAllCompanies,
} from '../features/auth/authSlice';

import {
  resolveBootstrapCompanyID,
} from '../utils/resolveCompanyContext';


const FullScreenLoading = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <div
      className="spinner-border text-primary"
      role="status"
    >
      <span className="visually-hidden">
        Loading...
      </span>
    </div>
  </div>
);


const PrivateRoute = ({
  children,
  requiredRole,
}) => {
  const location =
    useLocation();

  const dispatch =
    useDispatch();

  const {
    user,
    accessToken,
    _persist,
  } = useSelector(
    (state) => state.auth,
  );

  const rehydrated =
    _persist?.rehydrated;


  /**
   * 当前 PrivateRoute 是否正在恢复 session。
   */
  const [
    sessionLoading,
    setSessionLoading,
  ] = useState(false);


  /**
   * 当前浏览器 tab 是否已经执行过一次
   * session bootstrap。
   *
   * 非常重要：
   *
   * 即使 refresh 失败，也要设为 true，
   * 否则 effect 会一直重复调用 refresh。
   */
  const [
    sessionChecked,
    setSessionChecked,
  ] = useState(false);


  /**
   * 防止 effect 并发执行。
   */
  const inFlightRef =
    useRef(false);


  useEffect(() => {
    let cancelled =
      false;


    const bootstrapSession =
      async () => {
        /**
         * 必须先等 redux-persist rehydrate。
         *
         * 当前 tab 可能已经有 user/companyID。
         */
        if (!rehydrated) {
          return;
        }


        /**
         * 当前 tab 已完成 bootstrap。
         */
        if (
          sessionChecked
        ) {
          return;
        }


        /**
         * 防止 React StrictMode
         * 或重复 render 产生并发 bootstrap。
         */
        if (
          inFlightRef.current
        ) {
          return;
        }


        inFlightRef.current =
          true;

        setSessionLoading(
          true,
        );


        try {
          /**
           * accessToken 没有 persist，
           * 所以：
           *
           * - 刷新页面
           * - 新标签页
           * - 复制标签页
           *
           * 都可能没有 accessToken。
           *
           * 此时不能直接跳 Login，
           * 应该先尝试用 httpOnly refresh cookie
           * 恢复 accessToken。
           */
          if (!accessToken) {
            await refreshAccessTokenRequest();

            if (cancelled) {
              return;
            }
          }


          /**
           * 当前 tab 没有 user 时，
           * 重新读取 /users/me。
           *
           * 普通 USER：
           * 这里会恢复 companyID。
           *
           * ADMIN：
           * 这里会恢复 allCompanies，
           * companyID 再通过 URL / Redux context 决定。
           */
          if (!user) {
            const me =
              await getUserInfo();

            if (!me) {
              throw new Error(
                'No user info',
              );
            }


            if (cancelled) {
              return;
            }


            dispatch(
              setUser(
                me,
              ),
            );


            dispatch(
              setAllCompanies(
                me.allCompanies ||
                [],
              ),
            );


            const bootstrapCompanyID =
              resolveBootstrapCompanyID({
                user: me,

                search:
                  window.location
                    .search,
              });


            dispatch(
              setCompanyID(
                bootstrapCompanyID,
              ),
            );
          }
        } catch (error) {
          /**
           * refresh / users/me 都失败，
           * 当前 session 才真正视为失效。
           */
          if (!cancelled) {
            dispatch(
              logout(),
            );
          }
        } finally {
          inFlightRef.current =
            false;


          if (!cancelled) {
            setSessionLoading(
              false,
            );

            setSessionChecked(
              true,
            );
          }
        }
      };


    bootstrapSession();


    return () => {
      cancelled =
        true;
    };
  }, [
    rehydrated,
    sessionChecked,
    accessToken,
    user,
    dispatch,
  ]);


  /**
   * 第一步：
   * 等 redux-persist。
   */
  if (!rehydrated) {
    return (
      <FullScreenLoading />
    );
  }


  /**
   * 第二步：
   * 给 PrivateRoute 一次机会，
   * 使用 refresh cookie 恢复 session。
   *
   * 不能在这里提前根据
   * !accessToken 跳 Login。
   */
  if (
    !sessionChecked ||
    sessionLoading
  ) {
    return (
      <FullScreenLoading />
    );
  }


  /**
   * bootstrap 已经执行完，
   * Redux 里仍然没有 accessToken：
   *
   * refresh cookie 无效，
   * 才真正跳 Login。
   */
  if (!accessToken) {
    return (
      <Navigate
        to="/login"
        state={{
          from:
            location,
        }}
        replace
      />
    );
  }


  /**
   * 理论上 accessToken 存在时
   * user 也应该已经恢复。
   *
   * 做防御性保护。
   */
  if (!user) {
    return (
      <FullScreenLoading />
    );
  }


  /**
   * 页面角色权限。
   */
  if (
    requiredRole &&
    user?.role !==
    requiredRole
  ) {
    return (
      <Navigate
        to="/unauthorized"
        replace
      />
    );
  }


  return children;
};


export default PrivateRoute;